import { supabase } from '../supabase/client';

export interface EmailAnalyse {
  id: string;
  email_id: string;
  modell: string | null;
  absender_typ: string | null;
  cockpit_status?: 'offen' | 'freigegeben' | 'verworfen';
  mieter_id: string | null;
  eigentuemer_id: string | null;
  dienstleister_id: string | null;
  objekt_id: string | null;
  einheit_id: string | null;
  kategorie_id: string | null;
  dienstleister_sparte_id: string | null;
  prioritaet: string | null;
  ist_notfall: boolean | null;
  confidence: number | null;
  zusammenfassung: string | null;
  erkannte_aufgabe: string | null;
  regel_id: string | null;
  dienstleister_vorschlag_id: string | null;
  fehler: string | null;
  updated_at: string | null;
  naechster_schritt: string | null;
  bearbeiter_vorschlag_id: string | null;
  bearbeiter_vorschlag_typ: 'profile' | 'team' | null;
  ist_rechtlich_sensibel?: boolean;
  ist_werbung?: boolean;
  /** #489: erkannte Vorgangsart und Angaben aus der Mail */
  vorgangsart_id?: string | null;
  vorgang_angaben?: Record<string, string> | null;
}

export async function ladeAnalysenFuerEmails(emailIds: string[]): Promise<Record<string, EmailAnalyse>> {
  if (emailIds.length === 0) return {};
  // In Batches: .in() mit vielen UUIDs erzeugt sonst eine zu lange GET-URL (414 URI Too Long).
  const CHUNK = 100;
  const batches: string[][] = [];
  for (let i = 0; i < emailIds.length; i += CHUNK) batches.push(emailIds.slice(i, i + CHUNK));
  const results = await Promise.all(
    batches.map((b) => supabase.from('email_analysen').select('*').in('email_id', b)),
  );
  const map: Record<string, EmailAnalyse> = {};
  for (const { data, error } of results) {
    if (error) { console.error('Analysen laden fehlgeschlagen', error.message); continue; }
    for (const r of data || []) map[(r as EmailAnalyse).email_id] = r as EmailAnalyse;
  }
  return map;
}

export async function ladeKiModell(): Promise<'mistral' | 'gemini'> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'ki_modell').maybeSingle();
  return data?.value === 'gemini' ? 'gemini' : 'mistral';
}

export async function speichereKiModell(modell: 'mistral' | 'gemini'): Promise<void> {
  const { error } = await supabase.from('app_settings').upsert({ key: 'ki_modell', value: modell, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

export async function analysiereEmail(emailId: string, modell?: 'mistral' | 'gemini', force?: boolean): Promise<void> {
  const { error } = await supabase.functions.invoke('email-analyse', { body: { email_id: emailId, modell, force } });
  if (error) throw error;
}

// Lädt die Namen zum Dienstleister-Vorschlag + zur getroffenen Regel einer Analyse (separat, robust ohne FK-Embed).
export async function ladeVorschlagUndRegel(analyse: EmailAnalyse): Promise<{
  vorschlag: { name: string; telefon: string | null; email: string | null } | null;
  regel: { name: string } | null;
}> {
  let vorschlag: { name: string; telefon: string | null; email: string | null } | null = null;
  let regel: { name: string } | null = null;
  if (analyse.dienstleister_vorschlag_id) {
    const { data } = await supabase.from('dienstleister').select('name, telefon, email').eq('id', analyse.dienstleister_vorschlag_id).maybeSingle();
    vorschlag = (data as any) || null;
  }
  if (analyse.regel_id) {
    const { data } = await supabase.from('dienstleister_regeln').select('name').eq('id', analyse.regel_id).maybeSingle();
    regel = (data as any) || null;
  }
  return { vorschlag, regel };
}

// Auto-Zuordnungs-Schwelle (0..1) aus app_settings.
export async function ladeSchwelle(): Promise<number> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'ki_auto_zuordnung_schwelle').maybeSingle();
  const v: any = data?.value;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0.8;
}

export async function speichereSchwelle(wert: number): Promise<void> {
  const { error } = await supabase.from('app_settings').upsert({ key: 'ki_auto_zuordnung_schwelle', value: wert, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

export async function setzeCockpitStatus(analyseId: string, status: 'offen' | 'verworfen' | 'freigegeben'): Promise<void> {
  const { error } = await supabase.from('email_analysen').update({ cockpit_status: status }).eq('id', analyseId);
  if (error) throw error;
}

// Manuelles Setzen/Entfernen der Werbung-Kennzeichnung inkl. Zuordnung leeren bzw. Neu-Analyse.
export async function setzeWerbungManuell(emailId: string, wert: boolean): Promise<void> {
  const patch = wert
    ? { ist_werbung: true, objekt_id: null, mieter_id: null, eigentuemer_id: null }
    : { ist_werbung: false };
  const { error: e1 } = await supabase.from('emails').update(patch).eq('id', emailId);
  if (e1) throw e1;
  const { error: e2 } = await supabase.from('email_analysen').update({ ist_werbung: wert }).eq('email_id', emailId);
  if (e2) throw e2;
  if (!wert) {
    // Keine Werbung -> Mail korrekt (neu) zuordnen lassen.
    await analysiereEmail(emailId, undefined, true);
  }
}
